import { useMemo, useState } from 'react';
import type {
  TApiApplicationDocument,
  TRequiredUploadConfig,
  TRequireDocumentStatus,
} from '~/modules/covenants/types.ts';
import { REQUIRED_UPLOAD_CONFIG } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';

interface IUseRequiredDocumentUploadActionParams {
  doc: TApiApplicationDocument;
  isNewApplication: boolean;
  onUpload: () => void;
}

export const useRequiredDocumentUploadAction = ({
  doc,
  isNewApplication,
  onUpload,
}: IUseRequiredDocumentUploadActionParams) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const requiredUploadConfig = useMemo<TRequiredUploadConfig | null>(() => {
    if (!isNewApplication || !doc.requiredDocumentStatus) {
      return null;
    }

    return REQUIRED_UPLOAD_CONFIG[doc.requiredDocumentStatus];
  }, [doc.requiredDocumentStatus, isNewApplication]);

  const confirmableStatus = requiredUploadConfig?.requiresConfirmation
    ? (doc.requiredDocumentStatus as Exclude<TRequireDocumentStatus, 'PENDING'>)
    : null;

  const handleUploadClick = () => {
    if (!requiredUploadConfig?.requiresConfirmation) {
      onUpload();
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmUpload = () => {
    setIsConfirmModalOpen(false);
    onUpload();
  };

  const requiredUploadButtonVariant: 'contained' | 'outlined' =
    requiredUploadConfig?.tone === 'solid' ? 'contained' : 'outlined';
  const requiredUploadButtonSx =
    requiredUploadConfig?.tone === 'solid'
      ? appFormStyles.requiredSignBtn
      : appFormStyles.requiredPendingBtn;

  return {
    isConfirmModalOpen,
    requiredUploadConfig,
    confirmableStatus,
    requiredUploadButtonVariant,
    requiredUploadButtonSx,
    onUploadClick: handleUploadClick,
    onCloseConfirmModal: handleCloseConfirmModal,
    onConfirmUpload: handleConfirmUpload,
  };
};

